// Página de PLANES y precios. Lee los planes REALES configurados en la base de
// datos desde el endpoint público `GET /public/plans` (nombre público, precios
// mensual/anual, moneda y límites). Mantiene el conmutador mensual/anual, un FAQ
// de facturación y CTAs a /registro?plan=<code>. Si la carga falla, muestra un
// aviso elegante y un CTA de registro (sin romper la página).
// SEO: título + descripción + JSON-LD (FAQ de facturación).

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, Spinner } from '../../ui/components.js';
import { IMG } from '../media.js';
import { fetchPublicPlans, type PublicPlan } from '../publicApi.js';
import { Container, CtaBanner, FaqItem, SectionHeading } from '../components.js';
import { faqJsonLd, useSeo } from '../seo.js';
import { annualSavingPercent, formatPrice, monthlyEquivalentCents, type Billing } from '../planPricing.js';

/** Devuelve el límite de una métrica dentro de un plan (o undefined). */
function limitOf(plan: PublicPlan, metric: string) {
  return plan.limits.find((l) => l.metric === metric);
}

export function PricingView() {
  const { t, i18n } = useTranslation();

  /** Lo que DIFERENCIA a cada plan: solo discos máximos y acciones al mes. */
  function planLimits(plan: PublicPlan): { records: string; ops: string } {
    const records = limitOf(plan, 'catalog_records');
    const enrich = limitOf(plan, 'enrichment_ops');
    return {
      records:
        !records || records.maxCount == null
          ? t('marketing.pricing.recordsUnlimited')
          : t('marketing.pricing.records', { n: records.maxCount.toLocaleString(i18n.language) }),
      ops:
        !enrich || enrich.maxCount == null
          ? t('marketing.pricing.opsUnlimited')
          : t('marketing.pricing.ops', { n: enrich.maxCount.toLocaleString(i18n.language) }),
    };
  }

  // Funcionalidades COMUNES a todos los planes (se muestran una sola vez).
  const COMMON_FEATURES = [
    { icon: 'photo_camera', text: t('marketing.pricing.feat1') },
    { icon: 'auto_awesome', text: t('marketing.pricing.feat2') },
    { icon: 'content_copy', text: t('marketing.pricing.feat3') },
    { icon: 'bookmark', text: t('marketing.pricing.feat4') },
    { icon: 'query_stats', text: t('marketing.pricing.feat5') },
    { icon: 'notifications_active', text: t('marketing.pricing.feat6') },
    { icon: 'devices', text: t('marketing.pricing.feat7') },
    { icon: 'download', text: t('marketing.pricing.feat8') },
  ];

  const BILLING_FAQS = [
    { q: t('marketing.pricing.faq1Q'), a: t('marketing.pricing.faq1A') },
    { q: t('marketing.pricing.faq2Q'), a: t('marketing.pricing.faq2A') },
    { q: t('marketing.pricing.faq3Q'), a: t('marketing.pricing.faq3A') },
    { q: t('marketing.pricing.faq4Q'), a: t('marketing.pricing.faq4A') },
    { q: t('marketing.pricing.faq5Q'), a: t('marketing.pricing.faq5A') },
    { q: t('marketing.pricing.faq6Q'), a: t('marketing.pricing.faq6A') },
    { q: t('marketing.pricing.faq7Q'), a: t('marketing.pricing.faq7A') },
    { q: t('marketing.pricing.faq8Q'), a: t('marketing.pricing.faq8A') },
  ];

  // Por defecto mostramos el precio MENSUAL: es la cifra más baja y suele
  // convertir mejor. El usuario puede cambiar a anual para ver el ahorro.
  const [billing, setBilling] = useState<Billing>('monthly');
  const [plans, setPlans] = useState<PublicPlan[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useSeo({
    title: t('marketing.pricing.seoTitle'),
    description: t('marketing.pricing.seoDescription'),
    path: '/precios',
    image: IMG.coloredStack,
    keywords: t('marketing.pricing.seoKeywords', { returnObjects: true }) as string[],
    jsonLd: [faqJsonLd(BILLING_FAQS)],
  });

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    fetchPublicPlans(ctrl.signal)
      .then((items) => {
        // Orden por sort_order (el backend ya lo hace, reforzamos).
        setPlans([...items].sort((a, b) => a.sort_order - b.sort_order));
        setError(null);
      })
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') {
          setError(t('marketing.pricing.loadError'));
        }
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, []);

  // Ahorro MÁXIMO (%) del plan anual frente al mensual entre todos los planes.
  // Lo usamos en la burbuja de la pestaña "Anual" ("hasta -N%") para incentivar
  // la facturación anual (mejor retención/conversión). 0 → no hay ahorro.
  const maxAnnualSaving = useMemo(
    () => (plans ?? []).reduce((max, p) => Math.max(max, annualSavingPercent(p)), 0),
    [plans],
  );

  // Plan destacado: el de precio mensual más alto que no sea gratis, o el del medio.
  const highlightCode = useMemo(() => {
    const paid = (plans ?? []).filter((p) => p.price_cents_monthly > 0);
    if (paid.length === 0) return null;
    // El del medio por sort_order suele ser el "recomendado".
    return paid[Math.floor((paid.length - 1) / 2)]?.code ?? null;
  }, [plans]);

  return (
    <>
      {/* ===== Encabezado ===== */}
      <section className="py-space-xl lg:py-20">
        <Container className="flex flex-col items-center gap-space-lg">
          <SectionHeading
            eyebrow={t('marketing.pricing.eyebrow')}
            eyebrowIcon="workspace_premium"
            title={t('marketing.pricing.title')}
            subtitle={t('marketing.pricing.subtitle')}
          />

          {/* Conmutador mensual / anual */}
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-surface-deck border border-border-subtle">
            <button
              type="button"
              onClick={() => setBilling('monthly')}
              className={`px-4 h-9 rounded-full font-body-sm text-body-sm transition-colors ${
                billing === 'monthly' ? 'bg-surface-rack text-on-surface' : 'text-on-surface-variant'
              }`}
            >
              {t('marketing.pricing.monthly')}
            </button>
            <button
              type="button"
              onClick={() => setBilling('annual')}
              className={`relative px-4 h-9 rounded-full font-body-sm text-body-sm transition-colors ${
                billing === 'annual' ? 'bg-surface-rack text-on-surface' : 'text-on-surface-variant'
              }`}
            >
              {t('marketing.pricing.annual')}
              {/* Burbuja de descuento: incentiva la facturación anual mostrando
                  el ahorro MÁXIMO disponible ("hasta -N%"). Visible siempre que
                  haya ahorro, también desde la pestaña Mensual (default). */}
              {maxAnnualSaving > 0 && (
                <span className="absolute -top-2.5 -right-2 flex items-center gap-0.5 font-badge-micro text-badge-micro font-bold text-surface-base uppercase tracking-wider bg-goldmine-mint rounded-full px-2 py-0.5 shadow-[0_2px_8px_rgba(0,0,0,0.3)] whitespace-nowrap">
                  <Icon name="savings" className="text-[12px]" />
                  {t('marketing.pricing.upTo', { percent: maxAnnualSaving })}
                </span>
              )}
            </button>
          </div>

          {/* ===== Tarjetas de plan (desde la BD) ===== */}
          {loading && <Spinner label={t('marketing.pricing.loading')} />}

          {!loading && error && (
            <div className="w-full max-w-lg text-center flex flex-col items-center gap-space-md surface-card">
              <Icon name="cloud_off" className="text-[32px] text-on-surface-variant" />
              <p className="font-body-md text-body-md text-on-surface-variant">{error}</p>
              <Link to="/registro" className="btn-cyan">
                {t('marketing.cta.createAccountFree')} <Icon name="arrow_forward" className="text-[18px]" />
              </Link>
            </div>
          )}

          {!loading && !error && plans && plans.length > 0 && (
            <>
              {/* Tarjetas COMPACTAS: en escritorio los 5 planes caben en una
                  sola fila (grid de hasta 5 columnas). Cada tarjeta muestra solo
                  lo que diferencia a cada plan: capacidad de discos y acciones al
                  mes. Lo común se lista una vez debajo. */}
              <div
                className="w-full grid gap-space-sm mt-space-md items-stretch grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
              >
                {plans.map((plan) => {
                  const highlighted = plan.code === highlightCode;
                  // Siempre mostramos un precio "/mes": en anual es el total
                  // anual dividido entre 12 (equivalente mensual), no el total.
                  const priceLabel = formatPrice(
                    monthlyEquivalentCents(plan, billing),
                    plan.currency,
                  );
                  const isPaid = plan.price_cents_monthly > 0;
                  const limits = planLimits(plan);
                  return (
                    <div
                      key={plan.code}
                      className={`relative flex flex-col gap-space-sm rounded-xl p-space-md border ${
                        highlighted
                          ? 'border-primary-container bg-surface-deck shadow-[0_0_32px_rgba(56,189,248,0.15)]'
                          : 'border-border-subtle bg-surface-deck'
                      }`}
                    >
                      {highlighted && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-primary-container text-surface-base font-badge-micro text-badge-micro font-bold uppercase tracking-wider whitespace-nowrap">
                          {t('marketing.pricing.popular')}
                        </span>
                      )}
                      <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">{plan.name}</h3>

                      {/* Precio */}
                      <div className="flex items-baseline gap-1">
                        <span className="font-display-hero text-headline-lg-mobile text-on-surface font-bold tracking-tight">
                          {priceLabel}
                        </span>
                        {isPaid && (
                          <span className="font-body-sm text-body-sm text-on-surface-variant">{t('marketing.pricing.perMonth')}</span>
                        )}
                      </div>
                      {isPaid && (
                        <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider -mt-1">
                          {billing === 'annual' && plan.price_cents_yearly > 0
                            ? t('marketing.pricing.billedAnnual', {
                                amount: formatPrice(plan.price_cents_yearly, plan.currency),
                              })
                            : t('marketing.pricing.billedMonthly')}
                        </span>
                      )}

                      {/* Lo que diferencia a este plan */}
                      <div className="flex flex-col gap-space-xs mt-space-xs py-space-sm border-y border-border-subtle">
                        <div className="flex items-center gap-space-xs">
                          <Icon name="album" className="text-[18px] text-primary shrink-0" />
                          <span className="font-code-catalog text-body-sm text-on-surface font-semibold">{limits.records}</span>
                        </div>
                        <div className="flex items-center gap-space-xs">
                          <Icon name="bolt" className="text-[18px] text-vacuum-glow shrink-0" />
                          <span className="font-code-catalog text-body-sm text-on-surface font-semibold">{limits.ops}</span>
                        </div>
                      </div>

                      <Link
                        to={`/registro?plan=${encodeURIComponent(plan.code)}&billing=${billing}`}
                        className={`${highlighted ? 'btn-cyan' : 'btn-secondary'} w-full !h-10 !px-space-sm mt-auto`}
                      >
                        <span className="truncate">{isPaid ? t('marketing.pricing.choose') : t('marketing.pricing.start')}</span>
                        <Icon name="arrow_forward" className="text-[16px]" />
                      </Link>
                    </div>
                  );
                })}
              </div>

              {/* Barra de garantías/tranquilidad: resuelve objeciones comunes
                  (sin tarjeta para empezar, cancela cuando quieras, tus datos
                  son tuyos). Son afirmaciones reales del producto, no prueba
                  social inventada. */}
              <div className="w-full mt-space-md grid grid-cols-1 sm:grid-cols-3 gap-space-sm">
                {[
                  { icon: 'credit_card_off', text: t('marketing.pricing.guaranteeNoCard') },
                  { icon: 'cancel', text: t('marketing.pricing.guaranteeCancel') },
                  { icon: 'lock', text: t('marketing.pricing.guaranteeData') },
                ].map((g) => (
                  <div
                    key={g.icon}
                    className="flex items-center gap-space-xs rounded-xl border border-border-subtle bg-surface-deck px-space-md py-space-sm"
                  >
                    <Icon name={g.icon} className="text-[20px] text-goldmine-mint shrink-0" />
                    <span className="font-body-sm text-body-sm text-on-surface">{g.text}</span>
                  </div>
                ))}
              </div>

              {/* Funcionalidades COMUNES a todos los planes (una sola vez). */}
              <div className="w-full mt-space-lg rounded-xl border border-border-subtle bg-surface-deck p-space-lg">
                <div className="flex items-center gap-space-xs mb-space-md">
                  <Icon name="check_circle" className="text-[20px] text-goldmine-mint" />
                  <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">
                    {t('marketing.pricing.includedTitle')}
                  </h3>
                </div>
                <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
                  {t('marketing.pricing.includedBody')}
                </p>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-space-sm">
                  {COMMON_FEATURES.map((f) => (
                    <li key={f.text} className="flex items-start gap-space-xs font-body-sm text-body-sm text-on-surface">
                      <Icon name={f.icon} className="text-[18px] text-primary shrink-0 mt-px" />
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

        </Container>
      </section>

      {/* ===== FAQ de facturación ===== */}
      <section className="py-space-xl bg-surface-deck border-y border-border-subtle">
        <Container className="flex flex-col gap-space-lg max-w-3xl">
          <SectionHeading eyebrow={t('marketing.pricing.faqEyebrow')} eyebrowIcon="receipt_long" title={t('marketing.pricing.faqTitle')} />
          <div className="flex flex-col gap-space-sm">
            {BILLING_FAQS.map((f) => (
              <FaqItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Container>
      </section>

      <CtaBanner
        title={t('marketing.pricing.closingTitle')}
        subtitle={t('marketing.pricing.closingSubtitle')}
        primaryLabel={t('marketing.cta.createAccountFree')}
        secondaryTo="/funciones"
        secondaryLabel={t('marketing.cta.viewFeatures')}
      />
    </>
  );
}
